import React from 'react';
import { Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { 
    MENU_CATEGORIES, 
    RESERVATION_STATUSES, 
    REQUEST_STATUSES,
    formatDate,
    formatDateTime,
    getPriorityColor 
} from './diningConstants';

// Menu Item Modal
export function MenuItemModal({ modal, setModal, onSave }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, data: null })}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{modal.data?.id ? 'Edit Menu Item' : 'Add Menu Item'}</DialogTitle>
                </DialogHeader>
                {modal.data && (
                    <div className="space-y-4">
                        <div>
                            <Label>Name *</Label>
                            <Input 
                                value={modal.data.name} 
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, name: e.target.value }}))}
                                placeholder="e.g., Roast Chicken"
                                data-testid="menu-item-name"
                            />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea 
                                value={modal.data.description} 
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, description: e.target.value }}))}
                                placeholder="e.g., Free-range chicken with seasonal vegetables"
                                rows={2}
                                data-testid="menu-item-description"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Category *</Label>
                                <select 
                                    className="w-full border rounded-md p-2"
                                    value={modal.data.category}
                                    onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, category: e.target.value }}))}
                                    data-testid="menu-item-category"
                                >
                                    {MENU_CATEGORIES.map(cat => (
                                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label>Price</Label>
                                <Input 
                                    value={modal.data.price} 
                                    onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, price: e.target.value }}))}
                                    placeholder="£16.50"
                                    data-testid="menu-item-price"
                                />
                            </div>
                        </div>
                        <div className="space-y-3">
                            <Label>Dietary Options</Label>
                            <div className="flex flex-wrap gap-4">
                                <div className="flex items-center gap-2">
                                    <Switch 
                                        checked={modal.data.is_vegetarian}
                                        onCheckedChange={(checked) => setModal(prev => ({ ...prev, data: { ...prev.data, is_vegetarian: checked }}))}
                                    />
                                    <Label className="text-sm">Vegetarian</Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch 
                                        checked={modal.data.is_vegan}
                                        onCheckedChange={(checked) => setModal(prev => ({ ...prev, data: { ...prev.data, is_vegan: checked }}))}
                                    />
                                    <Label className="text-sm">Vegan</Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch 
                                        checked={modal.data.is_gluten_free}
                                        onCheckedChange={(checked) => setModal(prev => ({ ...prev, data: { ...prev.data, is_gluten_free: checked }}))}
                                    />
                                    <Label className="text-sm">Gluten Free</Label>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch 
                                checked={modal.data.is_available}
                                onCheckedChange={(checked) => setModal(prev => ({ ...prev, data: { ...prev.data, is_available: checked }}))}
                            />
                            <Label>Available</Label>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, data: null })}>Cancel</Button>
                    <Button onClick={onSave} className="bg-rose-600 hover:bg-rose-700" data-testid="save-menu-item-btn">
                        {modal.data?.id ? 'Update' : 'Add'} Item
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Daily Menu Modal
export function DailyMenuModal({ modal, setModal, menuItems, onSave }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, data: null })}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Set Daily Menu</DialogTitle>
                </DialogHeader>
                {modal.data && (
                    <div className="space-y-4">
                        <div>
                            <Label>Date</Label>
                            <Input 
                                type="date"
                                value={modal.data.date} 
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, date: e.target.value }}))}
                                data-testid="daily-menu-date"
                            />
                        </div>
                        <div>
                            <Label>Chef's Special Message</Label>
                            <Textarea 
                                value={modal.data.chef_special} 
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, chef_special: e.target.value }}))}
                                placeholder="Today's special: Fresh caught sea bass with Mediterranean vegetables..."
                                rows={3}
                                data-testid="daily-menu-special"
                            />
                        </div>
                        <div>
                            <Label>Select Menu Items</Label>
                            <div className="mt-2 max-h-60 overflow-y-auto border rounded-lg p-3 space-y-2">
                                {menuItems.filter(i => i.is_available).map(item => (
                                    <label key={item.id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-stone-50 rounded">
                                        <input 
                                            type="checkbox"
                                            checked={modal.data.menu_item_ids?.includes(item.id)}
                                            onChange={(e) => {
                                                const ids = modal.data.menu_item_ids || [];
                                                if (e.target.checked) {
                                                    setModal(prev => ({ ...prev, data: { ...prev.data, menu_item_ids: [...ids, item.id] }}));
                                                } else {
                                                    setModal(prev => ({ ...prev, data: { ...prev.data, menu_item_ids: ids.filter(id => id !== item.id) }}));
                                                }
                                            }}
                                            className="rounded"
                                        />
                                        <span className="flex-1">{item.name}</span>
                                        <span className="text-sm text-stone-500">{item.category}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, data: null })}>Cancel</Button>
                    <Button onClick={onSave} className="bg-rose-600 hover:bg-rose-700" data-testid="save-daily-menu-btn">
                        Save Daily Menu
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Dining Event Modal
export function DiningEventModal({ modal, setModal, onSave }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, data: null })}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Create Dining Event</DialogTitle>
                </DialogHeader>
                {modal.data && (
                    <div className="space-y-4">
                        <div>
                            <Label>Event Title *</Label>
                            <Input 
                                value={modal.data.title} 
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, title: e.target.value }}))}
                                placeholder="e.g., Wine Tasting Evening"
                                data-testid="dining-event-title"
                            />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea 
                                value={modal.data.description} 
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, description: e.target.value }}))}
                                placeholder="Join us for an evening of fine wines..."
                                rows={3}
                                data-testid="dining-event-description"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Date *</Label>
                                <Input 
                                    type="date"
                                    value={modal.data.date} 
                                    onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, date: e.target.value }}))}
                                    data-testid="dining-event-date"
                                />
                            </div>
                            <div>
                                <Label>Time</Label>
                                <Input 
                                    type="time"
                                    value={modal.data.time} 
                                    onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, time: e.target.value }}))}
                                    data-testid="dining-event-time"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Price</Label>
                                <Input 
                                    value={modal.data.price} 
                                    onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, price: e.target.value }}))}
                                    placeholder="£35 per person"
                                />
                            </div>
                            <div>
                                <Label>Capacity</Label>
                                <Input 
                                    type="number"
                                    value={modal.data.capacity} 
                                    onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, capacity: parseInt(e.target.value) || 20 }}))}
                                />
                            </div>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, data: null })}>Cancel</Button>
                    <Button onClick={onSave} className="bg-rose-600 hover:bg-rose-700" data-testid="save-dining-event-btn">
                        Create Event
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Reservation Modal
export function ReservationModal({ modal, setModal, onSave }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, data: null })}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Manage Reservation</DialogTitle>
                </DialogHeader>
                {modal.data && (
                    <div className="space-y-4">
                        <div className="bg-stone-50 rounded-lg p-4">
                            <p className="font-semibold">{formatDate(modal.data.date)} at {modal.data.time}</p>
                            <p className="text-sm text-stone-600">Party of {modal.data.party_size}</p>
                            {modal.data.special_requests && (
                                <p className="text-sm text-stone-500 mt-2 italic">"{modal.data.special_requests}"</p>
                            )}
                        </div>
                        <div>
                            <Label>Status</Label>
                            <select 
                                className="w-full border rounded-md p-2 mt-1"
                                value={modal.data.status}
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, status: e.target.value }}))}
                                data-testid="reservation-status"
                            >
                                {RESERVATION_STATUSES.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label>Table Number</Label>
                            <Input 
                                value={modal.data.table_number || ''} 
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, table_number: e.target.value }}))}
                                placeholder="e.g., 5"
                                data-testid="reservation-table"
                            />
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <Textarea 
                                value={modal.data.notes || ''} 
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, notes: e.target.value }}))}
                                placeholder="Internal notes..."
                                rows={2}
                            />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, data: null })}>Cancel</Button>
                    <Button onClick={onSave} className="bg-rose-600 hover:bg-rose-700" data-testid="save-reservation-btn">
                        Update Reservation
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Service Request Modal
export function ServiceRequestModal({ modal, setModal, newNote, setNewNote, onSave }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => { if (!open) { setModal({ open: false, data: null }); setNewNote(''); }}}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Update Service Request</DialogTitle>
                </DialogHeader>
                {modal.data && (
                    <div className="space-y-4">
                        <div className="bg-stone-50 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs px-2 py-0.5 rounded-full bg-stone-200 text-stone-600 capitalize">
                                    {modal.data.category?.replace('_', ' ')}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getPriorityColor(modal.data.priority)}`}>
                                    {modal.data.priority}
                                </span>
                            </div>
                            <p className="font-semibold">{modal.data.title}</p>
                            {modal.data.description && (
                                <p className="text-sm text-stone-600 mt-1">{modal.data.description}</p>
                            )}
                            <p className="text-xs text-stone-400 mt-2">
                                From: {modal.data.user?.email || modal.data.user_email || 'Unknown'} • {formatDateTime(modal.data.created_at)}
                            </p>
                        </div>
                        
                        {/* Existing notes */}
                        {modal.data.notes?.length > 0 && (
                            <div>
                                <Label className="mb-2 block">History</Label>
                                <div className="space-y-2 max-h-32 overflow-y-auto">
                                    {modal.data.notes.map((note, idx) => (
                                        <div key={idx} className="text-sm bg-blue-50 p-2 rounded">
                                            <span className="font-medium">{note.author}:</span> {note.note}
                                            <span className="text-xs text-stone-400 block">{formatDateTime(note.timestamp)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        <div>
                            <Label>Update Status</Label>
                            <select 
                                className="w-full border rounded-md p-2 mt-1"
                                value={modal.data.status}
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, status: e.target.value }}))}
                                data-testid="service-request-status"
                            >
                                {REQUEST_STATUSES.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label>Add Note</Label>
                            <Textarea 
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                placeholder="Add a note for this request..."
                                rows={2}
                                data-testid="service-request-note"
                            />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => { setModal({ open: false, data: null }); setNewNote(''); }}>Cancel</Button>
                    <Button onClick={onSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-service-request-btn">
                        <Send className="w-4 h-4 mr-2" />
                        Update Request
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Delete Confirmation Modal
export function DeleteConfirmModal({ deleteConfirm, setDeleteConfirm, onConfirm }) {
    return (
        <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: '', id: null, name: '' })}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Confirm Delete</DialogTitle>
                </DialogHeader>
                <p>Are you sure you want to delete "{deleteConfirm.name}"? This action cannot be undone.</p>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, type: '', id: null, name: '' })}>
                        Cancel
                    </Button>
                    <Button 
                        variant="destructive" 
                        onClick={onConfirm}
                        data-testid="confirm-delete-btn"
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
